import { createAction, createAsyncThunk, createSelector, createSlice, current } from '@reduxjs/toolkit';
import { setTokenCookie } from '@root/utils/cookies/cookies';
import { getAccountInfo } from 'hooks/account/useAccountInfo';
import { unionBy } from 'lodash';
import { CursiveRecorder, isElectron } from 'utils/electron';
import { UserPersona } from 'v2/types';

import accountService from '../services/accountService';
import errorParser from '../utils/errorHelpers';
import { trackUser } from '../utils/trackUser';
import { createTeam, editTeam, inviteUser, referUser } from './actions/teamActions';
import { deleteUsersFromOrganization, getTeamUsers, leaveTeam, requestAccess } from './teamSlice';

// TODO(GRO-899): Since getting teams is so slow, it can feel bad to load
//                more than one page. Once we speed up this endpoint, revert
//                this back to 25.
const DEFAULT_ORGS_PER_PAGE = 100;

export const updateAccountInfo = createAsyncThunk(
  'account/updateInfo',
  async (
    {
      firstName,
      lastName,
      oldPassword,
      password,
      confirmPassword,
      role,
      onboardedOnMobile,
      skippedRecordingExplainer,
      email,
      save,
      shared,
      showProfileCompletionCard,
      signedInThroughDesktopLink,
      isEnterprise,
      signupSource,
      personaDecision,
      updateLastActiveDate,
      onboardedWithProject,
      onboardingProjectName,
      ai_training_allowed
    },
    { rejectWithValue }
  ) => {
    return accountService
      .updateInfo({
        first_name: firstName,
        last_name: lastName,
        old_password: oldPassword,
        password,
        confirm_password: confirmPassword,
        role,
        onboarded_on_mobile: onboardedOnMobile,
        skipped_recording_explainer: skippedRecordingExplainer,
        email,
        save_scribe_id: save,
        shared_scribe_id: shared,
        show_profile_completion_card: showProfileCompletionCard,
        signed_in_through_desktop_link: signedInThroughDesktopLink,
        is_enterprise: isEnterprise,
        signup_source: signupSource,
        persona: personaDecision,
        update_last_active_date: updateLastActiveDate,
        onboarded_with_project: onboardedWithProject,
        onboarding_project_name: onboardingProjectName,
        ai_training_allowed
      })
      .then((res) => {
        if (!!res?.token && !!res?.refresh) {
          const { token, refresh } = res;

          setTokenCookie({ access: token, refresh });
        }
        if (signupSource && window.localStorage.getItem('signupSource')) {
          window.localStorage.removeItem('signupSource');
        }
        return res;
      })
      .catch((err) => {
        return rejectWithValue(err);
      });
  }
);

export const sendVerificationEmail = createAsyncThunk('account/sendVerificationEmail', async (email, { rejectWithValue }) => {
  try {
    return await accountService.sendVerificationEmail(email);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const verifyUserEmail = createAsyncThunk('account/verifyUserEmail', async ({ uuid }, { rejectWithValue }) => {
  try {
    return await accountService.verifyUserEmail(uuid);
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const fetchChangeEmailRequest = createAsyncThunk('account/fetchChangeEmailRequest', async (uuid, { rejectWithValue }) => {
  try {
    return await accountService.fetchChangeEmailRequest(uuid);
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * @deprecated This function is being deprecated and will be replaced by the useGrowthState hook.
 *
 * @typedef {Object} UpdateGrowthStatePayload
 * @property {boolean} [sharedFirstScribe]
 * @property {boolean} [acknowledgedInviteTeammate]
 * @property {boolean} [acknowledgedReferralProgramNotification]
 * @property {boolean} [acknowledgedReferralProgramBanner]
 * @property {boolean} [installedExtension]
 * @property {boolean} [installedDesktopRecorder]
 * @property {boolean} [celebratedActivation]
 * @property {boolean} [seenIntroModal]
 * @property {boolean} [seenWelcomeModal]
 * @property {boolean} [acknowledgedPromoCodeBanner]
 * @property {boolean} [acknowledgedMobileCodeModal]
 * @property {boolean} [acknowledgedZeroScribeLanding]
 * @property {boolean} [dismissedPinExtensionModal]
 * @property {boolean} [acknowledgedJoinOrganizations]
 * @property {boolean} [acknowledgedInstallExtension]
 * @property {boolean} [trackedCreatedFirstScribe]
 * @property {boolean} [acknowledgedTeaserUnlockedNotification]
 * @property {boolean} [acknowledgedTeaserEndedNotification]
 * @property {boolean} [acknowledgedTeamPlanUpsell]
 * @property {boolean} [seenReactionsTooltip]
 * @property {boolean} [seenMakePublicProfileTooltip]
 * @property {boolean} [seenWelcomeToScribeOnboardingModal]
 * @property {boolean} [celebratedProActivation]
 * @property {boolean} [hasEditedScreenshot]
 * @property {boolean} [hasEmbeddedScribe]
 * @property {boolean} [seenOfferTeamPlanModal]
 * @property {boolean} [updateLastSeenNewJoinableTeams]
 * @property {boolean} [extensionCurrentlyPinned]
 * @property {boolean} [isOnboarded]
 * @property {boolean} [acknowledgedGooglePermissions]
 * @property {null|string} [seenCommunityValueModal]
 * @property {null|string} [hdyhauAnswer]
 * @property {null|string} [proTrialStatus]
 */

export const updateGrowthState = createAsyncThunk(
  'account/updateGrowthState',
  /**
   * @param {UpdateGrowthStatePayload} payload
   * @returns {Promise<import('v2/types').FullScribeUser>}
   */
  async ({
    sharedFirstScribe,
    acknowledgedInviteTeammate,
    acknowledgedReferralProgramNotification,
    acknowledgedReferralProgramBanner,
    installedExtension,
    installedDesktopRecorder,
    celebratedActivation,
    seenIntroModal,
    seenWelcomeModal,
    acknowledgedPromoCodeBanner,
    acknowledgedMobileCodeModal,
    acknowledgedZeroScribeLanding,
    dismissedPinExtensionModal,
    acknowledgedJoinOrganizations,
    acknowledgedInstallExtension,
    trackedCreatedFirstScribe,
    acknowledgedTeaserUnlockedNotification,
    acknowledgedTeaserEndedNotification,
    acknowledgedTeamPlanUpsell,
    seenReactionsTooltip,
    seenWelcomeToScribeOnboardingModal,
    celebratedProActivation,
    hasEditedScreenshot,
    hasEmbeddedScribe,
    seenOfferTeamPlanModal,
    updateLastSeenNewJoinableTeams,
    seenMakePublicProfileTooltip,
    seenCommunityValueModal,
    hdyhauAnswer,
    trackedWorkspaceState,
    extensionCurrentlyPinned,
    proTrialStatus,
    isOnboarded,
    acknowledgedGooglePermissions
  }) => {
    const updatedInfo = await accountService.updateGrowthState({
      shared_first_scribe: sharedFirstScribe,
      acknowledged_invite_teammate: acknowledgedInviteTeammate,
      acknowledged_referral_program_notification: acknowledgedReferralProgramNotification,
      acknowledged_referral_program_banner: acknowledgedReferralProgramBanner,
      installed_extension: installedExtension,
      installed_desktop_recorder: installedDesktopRecorder,
      celebrated_activation: celebratedActivation,
      seen_intro_modal: seenIntroModal,
      seen_welcome_modal: seenWelcomeModal,
      acknowledged_promo_code_banner: acknowledgedPromoCodeBanner,
      acknowledged_mobile_code_modal: acknowledgedMobileCodeModal,
      acknowledged_zero_scribe_landing: acknowledgedZeroScribeLanding,
      dismissed_pin_extension_modal: dismissedPinExtensionModal,
      acknowledged_join_organizations: acknowledgedJoinOrganizations,
      acknowledged_install_extension: acknowledgedInstallExtension,
      tracked_created_first_scribe: trackedCreatedFirstScribe,
      acknowledged_teaser_unlocked_notification: acknowledgedTeaserUnlockedNotification,
      acknowledged_teaser_ended_notification: acknowledgedTeaserEndedNotification,
      acknowledged_team_plan_upsell: acknowledgedTeamPlanUpsell,
      seen_reactions_tooltip: seenReactionsTooltip,
      seen_make_public_profile_tooltip: seenMakePublicProfileTooltip,
      seen_welcome_to_scribe_onboarding_modal: seenWelcomeToScribeOnboardingModal,
      celebrated_pro_activation: celebratedProActivation,
      has_edited_screenshot: hasEditedScreenshot,
      has_embedded_scribe: hasEmbeddedScribe,
      seen_offer_team_plan_modal: seenOfferTeamPlanModal,
      update_last_seen_new_joinable_teams: updateLastSeenNewJoinableTeams,
      seen_community_value_modal: seenCommunityValueModal,
      hdyhau_answer: hdyhauAnswer,
      tracked_workspace_state: trackedWorkspaceState,
      extension_currently_pinned: extensionCurrentlyPinned,
      pro_trial_status: proTrialStatus,
      is_onboarded: isOnboarded,
      acknowledged_google_permissions: acknowledgedGooglePermissions
    });

    return updatedInfo;
  }
);

export const updateFeaturePresentation = createAsyncThunk('account/updateFeaturePresentation', async ({ ...presentedFeature }) => {
  // presentedFeature should be an object with feature flag as key and boolean as value
  // object example { editor_action_palette_dnd_presented: true/false }
  return accountService.updateFeaturePresentation({ ...presentedFeature });
});

export const affiliateSignup = createAsyncThunk('account/affiliateSignup', async (referring_user_id) => {
  await accountService.affiliateSignup(referring_user_id);
});

export const scheduleDesktopReminder = createAsyncThunk('account/scheduleDesktopReminder', async () => {
  await accountService.scheduleDesktopReminder();
});

export const getJoinableTeams = createAsyncThunk(
  'account/getJoinableTeams',
  async ({ searchQuery, skip, limit, excludeAdded = false, excludeInactive = false } = {}) => {
    const teams = await accountService.getJoinableTeams({
      searchQuery,
      skip,
      getFirst: limit || DEFAULT_ORGS_PER_PAGE,
      excludeAdded,
      excludeInactive
    });

    return teams;
  }
);

export const resetJoinableTeams = createAction('account/resetJoinableTeams');

export const getAllowedDomain = createAsyncThunk('account/getAllowedTeam', async () => {
  const link = await accountService.getAllowedDomain();

  return link;
});

export const getAllUserTeams = createAsyncThunk('account/getAllUserTeams', async ({ searchQuery, skip, limit } = {}) => {
  const allTeams = await accountService.listUserOrganizations({
    searchQuery,
    skip,
    getFirst: limit || DEFAULT_ORGS_PER_PAGE
  });

  return allTeams;
});

// USER NOTIFICATIONS //
export const NOTIFICATION_TYPES = {
  views: 'notify_views',
  comments: 'notify_comments',
  claps: 'notify_claps',
  stats: 'notify_stats',
  invites: 'notify_invites',
  referrals: 'notify_referrals',
  disable: 'disable_non_essential_notifications'
};
export const updateUserSettings = createAsyncThunk('userSettings/updateUserSettings', async (data) => {
  const userSettings = accountService.updateUserSettings(data);

  return userSettings;
});

// ****************** //

export const getAnalytics = createAsyncThunk('account/getAnalytics', async () => {
  const analytics = accountService.getAnalytics();

  return analytics;
});

const updateAccountActiveOrganization = (account, newActiveOrg) => {
  const updatedAccount = { ...account, active_organization: newActiveOrg };

  return updatedAccount;
};

export const setShowStickyActivationChecklist = createAction('account/setShowStickyActivationChecklist', (value) => {
  return {
    payload: value
  };
});

export const getUserDocumentsForActiveTeam = createAsyncThunk('account/getUserDocumentsForActiveTeam', async () => {
  const docs = await accountService.getUserDocumentsForActiveTeam();

  return docs;
});

export const checkForJoinableTeams = createAsyncThunk('account/checkForJoinableTeams', async () => {
  const hasNewJoinableTeams = await accountService.checkForJoinableTeams();

  return hasNewJoinableTeams;
});

export const INITIAL_STATE = {
  data: null,
  stats: {
    totalNumberScribes: 0,
    totalScribeViews: 0,
    totalScribeReactions: 0,
    mostClappedScribe: null,
    mostViewedScribe: null
  },
  previousData: null,
  isLoading: false,
  isError: false,
  errorMessage: null,
  affiliateLink: '',
  joinableTeams: null,
  loadingJoinableTeams: false,
  loadedAllJoinableTeams: false,
  totalJoinableTeams: 0,
  allTeams: [],
  loadingAllTeams: false,
  loadedAllTeams: false,
  totalAllTeams: 0,
  searchTeamsQuery: '',
  loadingAllowedDomain: false,
  allowedDomain: null,
  referrals: [],
  showStickyActivationChecklist: true,
  userDocumentsInActiveTeam: [],
  loadingUserDocuments: false
};

export const accountSlice = createSlice({
  name: 'account',
  initialState: { ...INITIAL_STATE },
  reducers: {
    setAccountInfo: (state, action) => {
      state.data = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountInfo.fulfilled, (state, action) => {
        // Add user to the state array
        state.data = action.payload;
        state.referrals = action.payload.referrals;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
        if (isElectron() && CursiveRecorder().getFullRecorderDetails) {
          CursiveRecorder()
            .getFullRecorderDetails()
            .then((recordingDetails) => {
              trackUser(action.payload, null, recordingDetails);
            });
        } else {
          trackUser(action.payload);
        }
      })
      .addCase(getAccountInfo.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAccountInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload && action.payload.code ? action.payload.code : action.payload;
      })
      .addCase(sendVerificationEmail.fulfilled, (state, action) => {
        // Add user to the state array
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(sendVerificationEmail.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(sendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload && action.payload.code ? action.payload.code : action.payload;
      })
      .addCase(verifyUserEmail.fulfilled, (state, action) => {
        state.data = action.payload.email;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(verifyUserEmail.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(verifyUserEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload && action.payload.code ? action.payload.code : action.payload;
      })
      .addCase(fetchChangeEmailRequest.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchChangeEmailRequest.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(fetchChangeEmailRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload && action.payload.code ? action.payload.code : action.payload;
      })
      .addCase(updateAccountInfo.fulfilled, (state, action) => {
        // Add user to the state array
        state.data = action.payload;
        state.previousData = null;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
        const { persona } = action.payload;

        if (persona === UserPersona.Operator) {
          state.showStickyActivationChecklist = false;
        }
      })
      .addCase(updateAccountInfo.pending, (state, action) => {
        const { firstName, lastName } = action.meta.arg;

        state.previousData = state.data;
        if (firstName || lastName) {
          state.data = { ...state.data, first_name: firstName, last_name: lastName };
        }
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateAccountInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
        state.data = state.previousData;
        state.previousData = null;
      })
      .addCase(updateFeaturePresentation.pending, (state, action) => {
        state.data = {
          ...state.data,
          feature_presentation: {
            ...(state?.data?.feature_presentation || {}),
            editor_action_palette_dnd_presented: true
          }
        };
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateFeaturePresentation.fulfilled, (state, action) => {
        // Add user to the state array
        state.data = {
          ...state.data,
          feature_presentation: {
            ...(state?.data?.feature_presentation || {}),
            ...(action.payload || {})
          }
        };
        state.previousData = null;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateGrowthState.fulfilled, (state, action) => {
        // Add user to the state array
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateGrowthState.pending, (state, action) => {
        if (action?.meta?.arg?.updateLastSeenNewJoinableTeams) {
          state.joinableTeams = [...state.joinableTeams].map((team) => {
            return { ...team, is_new_joinable: false };
          });
        }
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateGrowthState.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(inviteUser.fulfilled, (state, action) => {
        state.data = { ...state.data, invited_teammate: true };
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(referUser.fulfilled, (state, action) => {
        state.referrals = [...state.referrals, action.payload];
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        if (state.data?.organizations) {
          state.data.organizations = [...state.data.organizations, action.payload];
        } else {
          state.data = {
            ...state.data,
            organizations: [action.payload]
          };
        }
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createTeam.pending, (state) => {
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'accountSlice.createTeam');
      })
      .addCase(editTeam.fulfilled, (state, action) => {
        state.data = updateAccountActiveOrganization(state.data, action.payload);
        state.isError = false;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(editTeam.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(editTeam.rejected, (state, action) => {
        state.isError = true;
        state.isLoading = false;
        state.errorMessage = errorParser(action.payload, 'accountSlice.editTeam');
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.data = { ...state.data, ...action.payload };
      })
      .addCase(getAnalytics.fulfilled, (state, action) => {
        const { total_num_scribes, total_views, total_reactions, most_clapped_scribe, most_viewed_scribe } = action.payload;
        const newStats = {
          totalNumberScribes: total_num_scribes,
          totalScribeViews: total_views,
          totalScribeReactions: total_reactions,
          mostViewedScribe: most_viewed_scribe,
          mostClappedScribe: most_clapped_scribe
        };

        state.stats = newStats;
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAnalytics.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(resetJoinableTeams, (state) => {
        state.joinableTeams = null;
        state.loadingJoinableTeams = false;
        state.loadedAllJoinableTeams = false;
        state.totalJoinableTeams = 0;
        state.isError = false;
        state.errorMessage = null;
        state.searchTeamsQuery = '';
      })
      .addCase(getJoinableTeams.fulfilled, (state, action) => {
        const { organizations, count } = action.payload;
        const query = action.meta?.arg?.query || '';
        const skip = action.meta?.arg?.skip;

        if (query !== state.searchTeamsQuery || !skip) {
          state.joinableTeams = [...organizations];
        } else {
          state.joinableTeams = unionBy(state.joinableTeams, organizations, 'id');
        }
        state.loadingJoinableTeams = false;
        state.loadedAllJoinableTeams = state.joinableTeams.length >= count;
        state.totalJoinableTeams = count;
        state.isError = false;
        state.errorMessage = null;
        state.searchTeamsQuery = query;
      })
      .addCase(getJoinableTeams.pending, (state) => {
        state.loadingJoinableTeams = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getJoinableTeams.rejected, (state) => {
        state.loadingJoinableTeams = false;
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(requestAccess.fulfilled, (state, action) => {
        const teams = current(state).joinableTeams;

        if (!teams) {
          return;
        }

        if (action.payload?.is_request) {
          const index = teams.findIndex((team) => team.id === action.payload?.organization.id);

          state.joinableTeams[index].has_requested_access = true;
        } else {
          const index = teams.findIndex((team) => team.id === action.payload?.id);

          // update users active team
          state.data = updateAccountActiveOrganization(state.data, action.payload);

          state.joinableTeams[index].joined = true;
          // update user count when user joins team
          state.joinableTeams[index].user_count += 1;
        }
      })
      .addCase(leaveTeam.fulfilled, (state, action) => {
        const teams = current(state).joinableTeams;

        if (!teams) {
          return;
        }

        const index = teams.findIndex((team) => team.id === action.payload[1]?.id);

        state.joinableTeams[index].joined = false;
        // update user count when user leaves team
        state.joinableTeams[index].user_count -= 1;
        // remove active team if user leaves active team and has no other teams
        if (state.data.active_organization?.id === action.payload[1]?.id) {
          state.data.active_organization = null;
        }
      })
      .addCase(getAllUserTeams.fulfilled, (state, action) => {
        const { organizations, count } = action.payload;
        const query = action.meta?.arg?.query || '';
        const skip = action.meta?.arg?.skip;

        if (query !== state.searchTeamsQuery || !skip) {
          state.allTeams = [...organizations];
        } else {
          state.allTeams = unionBy(state.allTeams, organizations, 'id');
        }
        state.loadingAllTeams = false;
        state.totalAllTeams = count;
        state.loadedAllTeams = state.allTeams.length >= count;
        state.searchTeamsQuery = query;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAllUserTeams.pending, (state) => {
        state.loadingAllTeams = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(getAllUserTeams.rejected, (state) => {
        state.loadingAllTeams = false;
        state.isError = true;
        state.errorMessage = null;
      })
      .addCase(getTeamUsers.fulfilled, (state, action) => {
        const { organizationId, teammates } = action.payload;
        // find and update the team with the new user count after getting the team users

        state.allTeams = state.allTeams?.map((team) => {
          if (team.id === organizationId) {
            const newUserCount = teammates?.length || team.user_count;

            return { ...team, user_count: newUserCount };
          }
          return team;
        });
      })
      .addCase(getAllowedDomain.pending, (state) => {
        state.loadingAllowedDomain = true;
      })
      .addCase(getAllowedDomain.fulfilled, (state, action) => {
        state.loadingAllowedDomain = false;
        state.allowedDomain = action.payload.domain;
      })
      .addCase(getAllowedDomain.rejected, (state) => {
        state.loadingAllowedDomain = false;
        state.allowedDomain = null;
      })
      .addCase(setShowStickyActivationChecklist, (state, action) => {
        state.showStickyActivationChecklist = !!action.payload;
      })
      .addCase(getUserDocumentsForActiveTeam.fulfilled, (state, action) => {
        state.userDocumentsInActiveTeam = action.payload;
        state.loadingUserDocuments = false;
      })
      .addCase(getUserDocumentsForActiveTeam.pending, (state) => {
        state.loadingUserDocuments = true;
      })
      .addCase(getUserDocumentsForActiveTeam.rejected, (state) => {
        state.userDocumentsInActiveTeam = [];
        state.loadingUserDocuments = true;
      })
      .addCase(deleteUsersFromOrganization.fulfilled, (state, action) => {
        state.allTeams = state.allTeams.map((team) => {
          if (team.id === action.payload.organizationId) {
            // update user count when user is deleted from team
            const newUserCount = team.user_count - 1;

            return { ...team, user_count: newUserCount };
          }
          return team;
        });
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

/**
 * @typedef {Object} UserDocument
 * @property {boolean} id
 * @property {import('v2/types').FileKind} kind
 */

/**
 * @deprecated This type is deprecated and will be removed in a future release.
 * Please use or update the `useAccountInfo` hook or create a new one using react-query instead.
 *
 * @typedef {Object} AccountSubState
 * @property {import('v2/types').FullScribeUser} account
 * @property {boolean} isError
 * @property {boolean} isLoading
 * @property {string | null} errorMessage
 * @property {string} affiliateLink
 * @property {Array<import('v2/types').TeamDirectoryOrganization>} joinableTeams
 * @property {boolean} loadingJoinableTeams
 * @property {boolean} loadedAllJoinableTeams
 * @property {Array<import('v2/types').TeamDirectoryOrganization>} allTeams
 * @property {boolean} loadingAllTeams
 * @property {boolean} loadedAllTeams
 * @property {Array<any>} referrals
 * @property {boolean} showStickyActivationChecklist
 * @property {Array<UserDocument>} userDocumentsInActiveTeam
 */
export const selectAccount = createSelector(
  [
    (state) => state.account.data,
    (state) => state.account.isError,
    (state) => state.account.isLoading,
    (state) => state.account.errorMessage,
    (state) => state.account.affiliateLink,
    (state) => state.account.joinableTeams,
    (state) => state.account.loadingJoinableTeams,
    (state) => state.account.loadedAllJoinableTeams,
    (state) => state.account.allTeams,
    (state) => state.account.loadedAllTeams,
    (state) => state.account.loadingAllTeams,
    (state) => state.account.referrals,
    (state) => state.account.showStickyActivationChecklist,
    (state) => state.account.userDocumentsInActiveTeam
  ],
  /**
   * @returns {AccountSubState}
   */
  (
    account,
    isError,
    isLoading,
    errorMessage,
    affiliateLink,
    joinableTeams,
    loadingJoinableTeams,
    loadedAllJoinableTeams,
    allTeams,
    loadedAllTeams,
    loadingAllTeams,
    referrals,
    showStickyActivationChecklist,
    userDocumentsInActiveTeam
  ) => ({
    account,
    isError,
    isLoading,
    errorMessage,
    affiliateLink,
    joinableTeams,
    loadingJoinableTeams,
    loadedAllJoinableTeams,
    allTeams,
    loadedAllTeams,
    loadingAllTeams,
    referrals: referrals || [],
    showStickyActivationChecklist,
    userDocumentsInActiveTeam
  })
);

export const selectAccountTeams = createSelector(
  [
    (state) => state.account.joinableTeams,
    (state) => state.account.loadingJoinableTeams,
    (state) => state.account.loadedAllJoinableTeams,
    (state) => state.account.loadingAllTeams,
    (state) => state.account.allTeams,
    (state) => state.account.loadedAllTeams,
    (state) => state.account.totalAllTeams,
    (state) => state.account.totalJoinableTeams,
    (state) => state.account.searchTeamsQuery
  ],
  (
    joinableTeams,
    loadingJoinableTeams,
    loadedAllJoinableTeams,
    loadingAllTeams,
    allTeams,
    loadedAllTeams,
    totalAllTeams,
    totalJoinableTeams,
    searchTeamsQuery
  ) => ({
    joinableTeams,
    loadingJoinableTeams,
    loadedAllJoinableTeams,
    loadingAllTeams,
    allTeams,
    loadedAllTeams,
    totalAllTeams,
    totalJoinableTeams,
    searchTeamsQuery
  })
);

export const selectAllowedDomain = createSelector(
  [(state) => state.account.allowedDomain, (state) => state.account.loadingAllowedDomain],
  (allowedDomain, loadingAllowedDomain) => ({ allowedDomain, loadingAllowedDomain })
);

export default accountSlice.reducer;
